import { useEffect, useState } from 'react'
import { fxCustomersApi, fxExpensesApi, fxReportsApi } from '../../services/api'
import {
  LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import { Users, Megaphone } from 'lucide-react'
import KpiCard from '../../components/KpiCard'

const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1']

interface SalesTrendPoint {
  date: string
  total: number
}

interface TopProduct {
  product_id: number
  product_name: string
  quantity: number
  revenue: number
}

/** Small landing dashboard for FX Admin -- 2 KPI tiles + 2 charts, all-time
 * (no date-range picker yet, see task writeup for why). Mirrors the
 * coffee business's AdminDashboardPage conventions at a much smaller scale. */
export default function FxDashboardPage() {
  const [activeCustomers, setActiveCustomers] = useState(0)
  const [serviceCostsTotal, setServiceCostsTotal] = useState(0)
  const [salesTrend, setSalesTrend] = useState<SalesTrendPoint[]>([])
  const [topProducts, setTopProducts] = useState<TopProduct[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [customersRes, expensesRes, salesTrendRes, topProductsRes] = await Promise.all([
          fxCustomersApi.list(),
          fxExpensesApi.list(),
          fxReportsApi.getSalesTrend(),
          fxReportsApi.getTopProducts(),
        ])

        const customers = customersRes.data.customers || []
        setActiveCustomers(customers.filter((c: any) => c.is_active === true).length)

        // Same filter/sum logic as ServiceCostsPage.tsx's `summary.total`:
        // expenses whose joined supplier_type is "servicio", restricted to
        // active (non-anulado) rows. No need to fetch suppliers separately --
        // supplier_type already comes joined on each expense row, which is
        // exactly how that page computes it too (suppliers there are only
        // used to populate the "new service cost" form's dropdown).
        const expenses = expensesRes.data.expenses || []
        const serviceCosts = expenses.filter((e: any) => e.supplier_type === 'servicio' && e.is_active)
        setServiceCostsTotal(serviceCosts.reduce((sum: number, e: any) => sum + Number(e.amount), 0))

        setSalesTrend(salesTrendRes.data.trend || [])
        setTopProducts(topProductsRes.data.products || [])
      } catch (err) {
        console.error('Error fetching FX dashboard data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, [])

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Panel Principal — FX Admin</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <KpiCard
          icon={Users}
          label="Clientes Activos"
          value={activeCustomers}
          accent="blue"
        />
        <KpiCard
          icon={Megaphone}
          label="Costos de Servicios"
          value={`S/ ${serviceCostsTotal.toFixed(2)}`}
          accent="amber"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <h3 className="text-lg font-medium text-espresso-800 mb-4">Ventas en el Tiempo</h3>
          {salesTrend.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas disponibles.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => [`S/ ${Number(value).toFixed(2)}`, 'Ingresos']} />
                <Legend />
                <Line type="monotone" dataKey="total" stroke="#f59e0b" strokeWidth={2} name="Ingresos (S/)" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <h3 className="text-lg font-medium text-espresso-800 mb-4">Top Productos</h3>
          {topProducts.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas disponibles.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProducts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="product_name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip
                  formatter={(value: number, _name: string, props: any) => [
                    `S/ ${Number(value).toFixed(2)} (${props.payload.quantity} unid.)`,
                    'Ingresos',
                  ]}
                />
                <Bar dataKey="revenue" name="Ingresos (S/)" radius={[0, 4, 4, 0]}>
                  {topProducts.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  )
}
